import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { TagModule } from "primeng/tag";
import { DrawerModule } from "primeng/drawer";
import { SelectModule } from "primeng/select";
import { TooltipModule } from "primeng/tooltip";
import { RatingModule } from "primeng/rating";
import { NgxPaginationModule } from "ngx-pagination";
import {
  RowAction,
  TableColumn,
  TableFilter,
  TagConfig,
} from "./data-table.model";

@Component({
  standalone: true,
  selector: "app-data-table",
  templateUrl: "./data-table.component.html",
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ConfirmDialogModule,
    TagModule,
    DrawerModule,
    SelectModule,
    TooltipModule,
    RatingModule,
    NgxPaginationModule,
  ],
})
export class DataTableComponent implements OnChanges {
  @Input() title = "";
  @Input() columns: TableColumn[] = [];
  @Input() filters: TableFilter[] = [];
  @Input() items: any[] = [];
  @Input() totalCount = 0;
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() loading = false;

  /** Seed filter values (e.g. from URL query params). */
  @Input() filterValues: Record<string, any> = {};

  /** Header create button. */
  @Input() createLink?: any[];
  @Input() createLabel = "Aggiungi";

  /** Default row edit action. */
  @Input() editLink?: (row: any) => any[];
  @Input() editLabel = "edit";
  /** Default row delete action. */
  @Input() deletable = true;

  /** Extra row actions rendered before edit/delete. */
  @Input() rowActions: RowAction[] = [];

  @Output() pageChange = new EventEmitter<number>();
  @Output() search = new EventEmitter<Record<string, any>>();
  @Output() delete = new EventEmitter<any>();
  @Output() action = new EventEmitter<{ event: string; row: any }>();

  filterSidebar = false;
  localFilters: Record<string, any> = {};
  readonly paginationId = "data-table-" + Math.random().toString(36).slice(2);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["filterValues"]) {
      this.localFilters = { ...(this.filterValues || {}) };
    }
  }

  get activeFilters(): TableFilter[] {
    return this.filters.filter((f) =>
      this.hasValue(this.localFilters[f.field]),
    );
  }

  applyFilters(): void {
    this.filterSidebar = false;
    this.search.emit(this.cleanValues());
  }

  resetFilters(): void {
    this.localFilters = {};
    this.filterSidebar = false;
    this.search.emit({});
  }

  clearFilter(field: string): void {
    delete this.localFilters[field];
    this.search.emit(this.cleanValues());
  }

  filterLabel(f: TableFilter): string {
    const val = this.localFilters[f.field];
    if (f.type === "select") {
      const opt = f.options?.find((o) => o.value === val);
      return opt ? opt.label : val;
    }
    return val;
  }

  cellValue(col: TableColumn, row: any): any {
    return col.value ? col.value(row) : row[col.field];
  }

  tagFor(col: TableColumn, row: any): TagConfig | null {
    if (col.tag) {
      return col.tag(row);
    }
    const raw = this.cellValue(col, row);
    if (col.tagMap && col.tagMap[raw] !== undefined) {
      return col.tagMap[raw];
    }
    if (col.type === "boolean") {
      return raw
        ? (col.trueTag ?? { label: "Si", severity: "success" })
        : (col.falseTag ?? { label: "No", severity: "danger" });
    }
    if (raw !== null && raw !== undefined && raw !== "") {
      return { label: String(raw), severity: "secondary" };
    }
    return null;
  }

  onRowAction(a: RowAction, row: any): void {
    if (a.event) {
      this.action.emit({ event: a.event, row });
    }
  }

  onDelete(row: any): void {
    this.delete.emit(row?.id ?? row);
  }

  onPageChange(p: number): void {
    this.pageChange.emit(p);
  }

  get rangeStart(): number {
    return this.totalCount === 0
      ? 0
      : this.pageSize * this.page - this.pageSize + 1;
  }

  /** Last row number on screen: based on the rows actually shown, not the page size. */
  get rangeEnd(): number {
    return this.rangeStart + (this.items?.length ?? 0) - 1;
  }

  /** Number of rows currently shown in the table. */
  get shownCount(): number {
    return this.items?.length ?? 0;
  }

  private hasValue(v: any): boolean {
    return v !== null && v !== undefined && v !== "";
  }

  private cleanValues(): Record<string, any> {
    const out: Record<string, any> = {};
    Object.entries(this.localFilters).forEach(([k, v]) => {
      if (this.hasValue(v)) out[k] = v;
    });
    return out;
  }
}
